import update from 'immutability-helper';
import _ from 'lodash';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CustomErrorBoundary } from 'src/components/widgets/customErrorBoundary';
import { Section } from 'src/components/widgets/section';

import { T, backofficeUrl, getUrlWithQueryParams, openFileDialog, stringFormat, toGlobalID, toLocalID, uploadFile, urlToFile, uuid } from 'src/shared/helpers';

import { dialogsPortal } from 'src/shared/portals';
import { Mutable } from 'src/shared/types';
import styled from 'styled-components';
import { EcommerceId } from '../../../../../shared/enums';
import PageErrorIcon from '../../../../../shared/icons/page-error-icon.svg';
import { Button, Link } from '../../../../widgets/buttons';
import { Icon } from '../../../../widgets/icon';
import { TextButton } from '../../../../widgets/buttons/textButton';
import { Dialog, DialogButton, DialogWindow, MessageDialog, useDialogManager } from '../../../../widgets/dialog';
import { PageFooterButtons } from '../../../../widgets/headers';
import { ContainerBase } from '../../../../widgets/layouts';
import { showNotification } from '../../../../widgets/notificationToaster';
import { Radio } from '../../../../widgets/radio';
import {
    addEmptyModelSideAction,
    addNewColorAction,
    editColorJsonSettingsAction,
    editColorMappingAction,
    editColorNameAction,
    editColorNameAndAttributesAction,
    editModelColorMappingsSalesChannelsAction,
    editSideMandatoryAllSides,
    editSideMandatoryAllVariants,
    editSideRotateAllSides,
    editSideRotateAllVariants,
    findModelColorForModelSide,
    getProductIsResizable,
    mergeModelSideAction,
    removeColorAction,
    removeModelOptionsAction,
    removeModelSideAction,
    setDisabledSidesAction,
    swapColorsAction,
    upsertModelOptionsAction,
    useProductConfigurationChangeStep,
    useProductConfigurationState
} from '../../state';
import { ModelColor, isDefaultColor as isDefaultModelColor } from '../../state/model/modelColor';
import { MerchantProductOption, ModelColorMapping } from '../../state/model/modelColorMapping';
import { ModelOptions } from '../../state/model/modelOptions';
import { ModelSide } from '../../state/model/modelSide';
import { useModelSideManager } from '../../state/model/modelSideManager';
import { subscriptionQuery } from '../../state/queries/__generated__/subscriptionQuery.graphql';
import { subscription } from '../../state/queries/subscription';
import SelectedOptionValue from '../../state/selectedOptionValue';
import { Side } from '../../state/side';
import { PrintAreaEditorDialog } from '../3-printAreas/printAreaEditorDialog';
import { LargeMessageDialogWindow } from '../3-printAreas/printAreas';
import ConnectModelDialog, { PartialSalesChannel } from '../6-done/connectModelDialog';
import { variantsMerchantProductOptionsQuery } from './__generated__/variantsMerchantProductOptionsQuery.graphql';
import { variantsQuery } from './__generated__/variantsQuery.graphql';
import { variantsSetModelColorMappingMutation } from './__generated__/variantsSetModelColorMappingMutation.graphql';
import AddVariant from './addVariant';
import EditVariantDialog from './editVariantDialog';
import { ColorAttribute, MerchantProductVariantImageMatch, buildCombinations, matchColorsAttributes, merchantProductOptionType } from './helpers';
import JSONSettingsDialog from './jsonSettingsDialog';
import EditVariantsOffline from './offlineProducts/editVariantsOffline';
import MenuSourceVariants from './offlineProducts/menuSourceVariants';
import VariantsList from './variantsList';

const CustomDialogWindow = styled(DialogWindow)`
    flex: 1;
    align-self: stretch;
    justify-self: stretch;
    justify-content: center;
    margin: 0 80px;
    width: auto;
`;

const MiddleContent = styled.div`
    overflow: auto;
    max-height: 200px;
`;

const VariantContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const NoVariantsMessage = styled.p`
    margin: 40px auto;
    max-width: 550px;
    text-align: center;
    line-height: 22px;
`;

const ErrorIcon = styled(Icon)`
    height: 80px;
    width: 80px;
    margin: 20px auto 0;
    display: block;
`;

const SideContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

interface SideForDialog {
    side: Side;
    firstCallback: () => void;
    allCallback: () => void;
    variantCallback: (color: string) => void;
    dialogKey: string;
}

interface Variant {
    code: string;
    side: Side;
    type: 'PRINT_AREA' | 'IMAGE';
    firstCallback: () => void;
    allCallback: () => void;
    variantCallback: (color: string) => void;
}
enum Select {
    all = 'all',
    thisOne = 'this one',
    thisOneInAllVariants = 'this one in all variants'
}

const VariantDialog: FC<Variant> = ({ code, side, firstCallback, allCallback, variantCallback }) => {
    const [selected, setSelected] = useState<string>(Select.thisOne);

    const { closeDialog } = useDialogManager();
    const codes = JSON.parse(code) as Array<Color>;

    const checkList =
        codes.length > 1 &&
        codes.map((x) => (
            <Radio
                label={T._('Change on all the variants with the attribute', 'Admin') + ' ' + (x.Value.Label ?? x.Value.Id)}
                checked={selected === x.Value.Id}
                onSelect={() => {
                    setSelected(x.Value.Id);
                }}
                name={x.Label}
                value={x.Value.Id}
            />
        ));
    const buttons: Array<DialogButton> = [
        {
            label: T._('Cancel', 'Admin'),
            outline: true,
            onClick: () => closeDialog('apply-image-variant')
        },
        {
            label: T._(`Apply`, 'Admin'),
            outline: false,
            onClick: () => {
                if (selected === Select.all) {
                    allCallback();
                    closeDialog('apply-image-variant');
                } else if (selected === Select.thisOne) {
                    firstCallback();
                    closeDialog('apply-image-variant');
                } else if (selected != Select.thisOne && selected != Select.all) {
                    variantCallback(selected);
                    closeDialog('apply-image-variant');
                }
            }
        }
    ];
    const message = stringFormat(T._('Select where you wish to apply the changes below:', 'Admin'), side.name);
    return (
        <Dialog windowDecorator={LargeMessageDialogWindow} title={message} buttons={buttons}>
            <MiddleContent>
                <VariantContainer>
                    <Radio
                        label={T._('Change on the current variant only', 'Admin')}
                        checked={selected === Select.thisOne}
                        onSelect={() => {
                            setSelected(Select.thisOne);
                        }}
                        name={'Change only this one'}
                        value={Select.thisOne}
                    />
                    <Radio
                        label={T._('Change on all the variants', 'Admin')}
                        checked={selected === Select.all}
                        onSelect={() => {
                            setSelected(Select.all);
                        }}
                        name={'Change All'}
                        value={Select.all}
                    />
                    {checkList && <>{checkList}</>}
                </VariantContainer>
            </MiddleContent>
        </Dialog>
    );
};

const SideDialog: FC<SideForDialog> = ({ side, firstCallback, allCallback, variantCallback, dialogKey }) => {
    const [selected, setSelected] = useState<string>(Select.thisOne);
    const { closeDialog } = useDialogManager();

    const buttons: Array<DialogButton> = [
        {
            label: T._('Cancel', 'Admin'),
            outline: true,
            onClick: () => closeDialog(dialogKey)
        },
        {
            label: T._(`Apply`, 'Admin'),
            outline: false,
            onClick: () => {
                if (selected === Select.all) {
                    allCallback();
                    closeDialog(dialogKey);
                } else if (selected === Select.thisOne) {
                    firstCallback();
                    closeDialog(dialogKey);
                } else if (selected != Select.thisOne && selected != Select.all) {
                    variantCallback(selected);
                    closeDialog(dialogKey);
                }
            }
        }
    ];
    const message = stringFormat(T._('Select where you wish to apply the changes below:', 'Admin'));

    return (
        <Dialog windowDecorator={LargeMessageDialogWindow} title={message} buttons={buttons}>
            <MiddleContent>
                <SideContainer>
                    <Radio
                        label={T._('Change on the current side only', 'Admin')}
                        checked={selected === Select.thisOne}
                        onSelect={() => {
                            setSelected(Select.thisOne);
                        }}
                        name={'Change only this one'}
                        value={Select.thisOne}
                    />
                    <Radio
                        label={T._('Change on all sides', 'Admin')}
                        checked={selected === Select.all}
                        onSelect={() => {
                            setSelected(Select.all);
                        }}
                        name={'Change all sides'}
                        value={Select.all}
                    />
                    <Radio
                        label={T._('Change on all sides named ' + side.name, 'Admin')}
                        checked={selected === Select.thisOneInAllVariants}
                        onSelect={() => {
                            setSelected(Select.thisOneInAllVariants);
                        }}
                        name={'Change on all sides named'}
                        value={Select.thisOneInAllVariants}
                    />
                </SideContainer>
            </MiddleContent>
        </Dialog>
    );
};

interface Value {
    Id: string;
    Label: string;
}
interface Color {
    Id: string;
    Label: string;
    Value: Value;
}
const VariantsPage: FC<{}> = () => {
    // Hooks
    const navigate = useNavigate();
    const location = useLocation();

    const changeStep = useProductConfigurationChangeStep();
    const { changeImage } = useModelSideManager();
    const { state, dispatch } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();
    const routeParams = useParams();

    // Queries
    const query = useLazyLoadQuery<variantsQuery>(
        graphql`
            query variantsQuery($sku: String!, $salesChannelID: ID!) {
                ecommerceSettings {
                    ecommerceName
                }

                salesChannels(status: ACTIVE, ignoreNoneECommerce: true) {
                    id
                    ecommerceSettings {
                        ecommerceID
                        ecommerceName
                        hasProductsApi
                        isMarketplace
                        remoteUrl
                        definitiveUrl
                    }
                }

                merchantProductOptions(sku: $sku, salesChannelID: $salesChannelID) {
                    optionID
                    label
                    isGlobal
                    values {
                        valueID
                        label
                    }
                }

                seller {
                    marketplace
                }
                generalSettings {
                    isSideImageScalingEnabled
                }
            }
        `,
        { sku: state.product.code || '', salesChannelID: state.salesChannelID }
    );

    const merchantProductOptionsConnected =
        state.product.id === null
            ? []
            : (useLazyLoadQuery<variantsMerchantProductOptionsQuery>(
                  graphql`
                      query variantsMerchantProductOptionsQuery($productID: ID!) {
                          model(id: $productID) {
                              merchantProductConnectedOptions {
                                  salesChannelID
                                  sku
                                  optionID
                                  label
                                  isGlobal
                                  values {
                                      valueID
                                      label
                                  }
                              }
                          }
                      }
                  `,
                  { productID: state.product.id! }
              ).model?.merchantProductConnectedOptions ?? []);

    // Mutations

    const setModelColorMappingMutation = graphql`
        mutation variantsSetModelColorMappingMutation($modelID: ID!, $modelColorMappings: [ModelColorMappingInput!]!) {
            setModelColorMapping(modelID: $modelID, modelColorMappings: $modelColorMappings) {
                colorID
                merchantCode
            }
        }
    `;

    const [setModelColorMappings, isMappingInProgress] = useMutation<variantsSetModelColorMappingMutation>(setModelColorMappingMutation);

    // State
    const [currentEditingModelSide, setCurrentEditingModelSide] = useState<ModelSide | null>(null);
    const [currentEditingModelOptions, setCurrentEditingModelOptions] = useState<ModelOptions | null>(null);

    const [modelOptions, setModelOptions] = useState<merchantProductOptionType[]>(() => {
        let modelColorsCodes = state.product.colors.map((c) => JSON.parse(c.code || '[]') as ColorAttribute[]);
        let modelOptions: merchantProductOptionType[] = [];
        if (state.product.colors.length > 0) {
            for (let i = 0; i < modelColorsCodes[0].length; i++) {
                modelOptions.push({
                    isGlobal: false,
                    optionID: modelColorsCodes[0][i].Id,
                    label: modelColorsCodes[0][i].Label,
                    values: []
                });
                modelColorsCodes.forEach((m) => {
                    m[i] &&
                        modelOptions[i].values.push({
                            valueID: m[i].Value.Id,
                            label: m[i].Value.Label
                        });
                });
                modelOptions[i].values = [...new Set(modelOptions[i].values.map((o) => JSON.stringify(o)))].map((o2) => JSON.parse(o2));
            }
        }

        return modelOptions;
    });

    const areVariantsSupported = state.supportedVariants;

    const hasMultipleOptions =
        query &&
        query.merchantProductOptions &&
        query.merchantProductOptions.length > 0 &&
        (query.merchantProductOptions.length > 1 || query.merchantProductOptions[0].values.length > 1);

    const hasCompleteAttributes = (() => {
        if (state.product.colors.length < 1 || state.product.colors[0].code === '[]') return false;
        try {
            const codes = JSON.parse(state.product.colors[0].code) as SelectedOptionValue[];
            return codes.length > 0 && codes.every((c) => c.Label !== '' && c.Value.Label !== '');
        } catch {
            return false;
        }
    })();
    const hasVariants = hasCompleteAttributes || hasMultipleOptions;
    const subscriptionResponse = useLazyLoadQuery<subscriptionQuery>(subscription, {});
    const [showMappingDialog, setShowMappingDialog] = useState(false);
    const [fixedVariantGuids, setFixedVariantGuids] = useState<Set<string>>(new Set());

    //This state was introduced to handle the variant images based on the default image size, without taking scaling settings into account at this stage.
    const [isDefaultImageScaled, setIsDefaultImageScaled] = useState<boolean>(false);

    const selectedSalesChannel = query.salesChannels.find((s) => s.id === state.salesChannelID)?.ecommerceSettings?.ecommerceID;

    const showBannerEcommerceIDs = [
        EcommerceId.MAGENTO2,
        EcommerceId.SHOPIFY,
        EcommerceId.PRESTASHOP,
        EcommerceId.BIGCOMMERCE,
        EcommerceId.WOOCOMMERCE,
        EcommerceId.ETSY
    ];

    let unsupportedSalesChannelsVariantsIDs = useMemo(() => {
        return query.salesChannels
            .filter((sc) => {
                return sc.ecommerceSettings?.ecommerceName === 'AMAZON';
            })
            .map((sc) => sc.id);
    }, [query.salesChannels]);

    //const showBanner = modelOptions?.length === 0; lascio commentato, se è true serve per mostrare l'info banner solo se non sono ancora state importate varianti

    const importImageVariantForSalesChannel = showBannerEcommerceIDs.includes(Number(selectedSalesChannel));

    // Detect mapping mismatch: compare variant attributes saved in Zakeke colors with current store options.
    // Computed once on mount so that editing individual variants doesn't clear the mismatch for the rest.
    // Skipped when the current sales channel has no catalog/products API: merchantProductOptions
    // would be empty or unreliable and the comparison would yield false positives.
    const currentSalesChannel = query.salesChannels.find((s) => s.id === state.salesChannelID);
    const currentHasCatalogApi = currentSalesChannel?.ecommerceSettings?.hasProductsApi ?? false;
    const mismatchedVariantGuids = useRef<Set<string> | null>(null);
    if (mismatchedVariantGuids.current === null) {
        const result = new Set<string>();
        if (currentHasCatalogApi) {
            const colorsWithAttributes = state.product.colors.filter((c) => c.code && c.code !== '[]');

            if (colorsWithAttributes.length > 0 && query.merchantProductOptions.length > 0) {
                const storeLabelByOptionID = new Map<string, string>();
                const storeValuesByOption = new Map<string, Set<string>>();
                for (const opt of query.merchantProductOptions) {
                    storeLabelByOptionID.set(opt.optionID, opt.label);
                    storeValuesByOption.set(opt.optionID, new Set(opt.values.map((v) => v.valueID)));
                }

                for (const color of colorsWithAttributes) {
                    const attrs = JSON.parse(color.code) as ColorAttribute[];
                    for (const attr of attrs) {
                        const storeLabel = storeLabelByOptionID.get(attr.Id);
                        const storeValues = storeValuesByOption.get(attr.Id);

                        if (
                            (storeLabel && storeLabel.toLowerCase() !== attr.Label.toLowerCase()) ||
                            (storeValues && attr.Value.Id && !storeValues.has(attr.Value.Id)) ||
                            (!storeLabel && attr.Id)
                        ) {
                            result.add(color.guid);
                            break;
                        }
                    }
                }
            }
        }
        mismatchedVariantGuids.current = result;
    }
    const hasMappingMismatch = mismatchedVariantGuids.current.size > 0 && mismatchedVariantGuids.current.size > fixedVariantGuids.size;

    const colors = useMemo(
        () =>
            _.sortBy(
                state.product.colors.filter((x) => !isDefaultModelColor(x)),
                (color) => color.position
            ),
        [state.product.colors]
    );

    const storeVariantsRemoved =
        areVariantsSupported &&
        !hasMultipleOptions &&
        hasCompleteAttributes &&
        colors.length > 0 &&
        !state.ecommerceSalesChannel.some((e) => e === 'NONE') &&
        (!query?.merchantProductOptions || query.merchantProductOptions.length === 0);

    //If all variants have been removed, redirect to the Review page with a notification.
    useEffect(() => {
        if (
            routeParams.page === 'variations' &&
            routeParams.subpage === 'list' &&
            colors.length === 0 &&
            !state.ecommerceSalesChannel.some((e) => e === 'NONE') &&
            state.ecommerceSalesChannel.length === 1
        ) {
            showNotification({
                type: 'success',
                message: <>{T._('Variations removed successfully. Remember to save to apply the changes.', 'Admin')}</>,
                dismiss: {
                    duration: 3000
                }
            });
        }
    }, [routeParams, colors, state.ecommerceSalesChannel, navigate]);

    async function wasDefaultImageScaled(url: string) {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        const load = new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
        });

        img.src = url;
        await load;

        const result = img.naturalWidth <= 1024 && img.naturalHeight <= 1024;
        return result;
    }

    useEffect(() => {
        const url = state.product?.colors?.[0]?.sides?.[0]?.imageUrl;

        if (!url) return;

        let cancelled = false;

        async function checkImage() {
            try {
                const result = await wasDefaultImageScaled(url);
                if (!cancelled) {
                    setIsDefaultImageScaled(result);
                }
            } catch (e) {
                console.error('Error checking image:', e);
            }
        }

        checkImage();
        return () => {
            cancelled = true;
        };
    }, []);

    // Auto-open print area editor when variantId and sideId are in the URL (from product sidebar)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const variantId = params.get('variantId');
        const sideIdParam = params.get('sideId');
        if (!variantId || !sideIdParam) return;

        const sideId = parseInt(sideIdParam, 10);
        const modelColor = state.product.colors.find((c) => c.colorID === parseInt(variantId, 10));
        if (!modelColor || isNaN(sideId)) return;

        const modelSide = modelColor.sides.find((s) => s.sideID === sideId);
        if (!modelSide) return;

        setCurrentEditingModelSide(modelSide);
        setCurrentEditingModelOptions(
            state.product.options.find((x) => x.colorGuid === modelColor.guid && (x.sideGuid === modelSide?.guid || x.sideGuid === null)) ?? null
        );
        navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/list/print-area-editor')), { state: location.state });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Callbacks
    const handleAddVariationClick = async (
        selectedOptionsValues: SelectedOptionValue[],
        merchantProductVariantImageMatches: MerchantProductVariantImageMatch[] | null
    ) => {
        if (selectedOptionsValues.length == 0) {
            showDialog('attributes-error', <MessageDialog message={T._('Select at last one attribute.', 'Admin')} />);
        } else {
            // Build combinations
            const combinations = buildCombinations(selectedOptionsValues);

            if (combinations.length > 300) {
                showNotification({
                    type: 'info',
                    message: (
                        <>
                            {T._(
                                'You are adding a large number of variations. This operation could take some time to complete. Please do not close the page.',
                                'Admin'
                            )}
                        </>
                    ),
                    dismiss: {
                        duration: 10000
                    }
                });
            }
            const skippedVariants: string[] = [];

            for (const combination of combinations) {
                // Check if variant already exists
                const alreadyExists = state.product.colors.some((x) => matchColorsAttributes(x.code, combination));
                if (alreadyExists) {
                    const variantName = combination.map((c) => c.Value.Label || c.Value.Id).join(' / ');
                    skippedVariants.push(variantName);
                    continue;
                }
                let imageUrl = null;
                if (merchantProductVariantImageMatches) {
                    const match = merchantProductVariantImageMatches.find(
                        (m) =>
                            m.selections &&
                            m.selections.every((s) =>
                                combination.some(
                                    (c) => (c.Id === s.optionID || c.Label === s.label) && (c.Value.Id === s.value.id || c.Value.Label === s.value.id)
                                )
                            )
                    );
                    if (match) {
                        let url = match.imageUrl;
                        if (isDefaultImageScaled) {
                            //convert store image url to File obj, to scale it if needed
                            const file = await urlToFile(match.imageUrl, 'store-image');
                            url = await uploadFile(file, isDefaultImageScaled);
                        }
                        imageUrl = url;
                    }
                }

                dispatch(addNewColorAction(combination, imageUrl));
            }

            if (skippedVariants.length > 0) {
                const allSkipped = skippedVariants.length === combinations.length;
                showNotification({
                    type: 'warning',
                    message: (
                        <>
                            {allSkipped
                                ? T._(
                                      'All selected variants have already been imported. If they are not visible, the existing variants may be mapped incorrectly. Click the edit icon on each variant to review and fix the mapping.',
                                      'Admin'
                                  )
                                : stringFormat(
                                      T._(
                                          'The following variants were not imported because they are already configured: {0}. If they are not visible, the existing variants may be mapped incorrectly. Click the edit icon on each variant to review and fix the mapping.',
                                          'Admin'
                                      ),
                                      skippedVariants.join(', ')
                                  )}
                        </>
                    ),
                    dismiss: {
                        duration: 10000
                    }
                });
            }

            let supportedAdditionalCodesForVariants = state.product.additionalCodes.filter((ac) => {
                return !unsupportedSalesChannelsVariantsIDs.some((x) => toLocalID(x) === toLocalID(ac.salesChannelId));
            });

            // If all variants were skipped, navigate to variants list
            if (skippedVariants.length === combinations.length) {
                navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/list/')));
                return;
            }

            if (state.product.id !== null && supportedAdditionalCodesForVariants.length > 0) setShowMappingDialog(true);
            else navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/list/')));
        }
    };

    const handleVariantRemoveClick = (modelColor: ModelColor) => {
        dispatch(removeColorAction(modelColor));
        let mappings = state.product.colorMappings.filter((m) => m.colorID != modelColor.colorID || (m.productColor && m.productColor.guid != modelColor.guid));
        dispatch(editModelColorMappingsSalesChannelsAction(mappings));
    };

    const handleVariantEditClick = (modelColor: ModelColor) => {
        showDialog(
            'color-name',
            <EditVariantDialog
                name={modelColor.name}
                mainSalesChannelID={state.salesChannelID}
                mainSalesChannelRemoteUrl={query.salesChannels.find((sc) => sc.id === state.salesChannelID)?.ecommerceSettings?.remoteUrl ?? 'Offline'}
                modelColor={modelColor}
                modelColorCode={JSON.parse(modelColor.code || '[]') as ColorAttribute[]}
                modelOptions={
                    state.ecommerceSalesChannel.some((e) => e !== 'NONE') && state.ecommerceSalesChannel.length == 1
                        ? (query.merchantProductOptions as merchantProductOptionType[])
                        : modelOptions
                }
                additionalCodes={state.product.additionalCodes.filter((ac) => {
                    return !unsupportedSalesChannelsVariantsIDs.some((x) => toLocalID(x) === toLocalID(ac.salesChannelId));
                })}
                colorMappings={state.product.colorMappings.filter((cm) => cm.colorID == modelColor.colorID || cm.productColor?.guid == modelColor.guid)}
                mainStoreVariantsRemoved={storeVariantsRemoved}
                onConfirm={(name, attributes, colorMapping, onError) => {
                    onConfirmVariantEdit(modelColor, name, attributes, colorMapping, onError);
                }}
            />
        );
    };

    const onConfirmVariantEdit = (
        modelColor: ModelColor,
        name: string,
        attributes: ColorAttribute[] | null | undefined,
        colorMapping: ModelColorMapping | null | undefined,
        onError: () => void
    ) => {
        if (colorMapping && state.product.id && attributes) {
            let newColorMappings = [...state.product.colorMappings];
            newColorMappings = newColorMappings.filter((mc) => mc.colorID != colorMapping.colorID || mc.productColor?.guid != colorMapping.productColor?.guid);
            newColorMappings.push(colorMapping);

            if (colorMapping.colorID > 0) {
                setModelColorMappings({
                    variables: {
                        modelID: state.product.id,
                        modelColorMappings: newColorMappings.map((mapp) => {
                            let { productColor, productSKU, ...newMapp } = mapp;
                            return newMapp;
                        })
                    },
                    onError: (error) => {
                        showNotification({
                            type: 'danger',
                            message: <>{T._('An error occurred while mapping product variants. Please try again.', 'Admin')}</>,
                            dismiss: {
                                duration: 3000
                            }
                        });
                        onError();
                    },
                    onCompleted: (response) => {
                        dispatch(editColorNameAndAttributesAction(modelColor, name, attributes));
                        dispatch(editColorMappingAction(colorMapping));
                        dispatch(editModelColorMappingsSalesChannelsAction(newColorMappings));
                        setFixedVariantGuids((prev) => new Set(prev).add(modelColor.guid));
                        closeDialog('color-name');
                    }
                });
            } else {
                dispatch(editColorNameAndAttributesAction(modelColor, name, attributes));
                dispatch(editColorMappingAction(colorMapping));
                dispatch(editModelColorMappingsSalesChannelsAction(newColorMappings));
                setFixedVariantGuids((prev) => new Set(prev).add(modelColor.guid));
                closeDialog('color-name');
            }
        } else if (attributes) {
            dispatch(editColorNameAndAttributesAction(modelColor, name, attributes));
            setFixedVariantGuids((prev) => new Set(prev).add(modelColor.guid));
            closeDialog('color-name');
        } else {
            dispatch(editColorNameAction(modelColor, name));
            setFixedVariantGuids((prev) => new Set(prev).add(modelColor.guid));
            closeDialog('color-name');
        }
    };

    const handleSideImageChangeClick = (modelSide: ModelSide) => {
        openFileDialog((files) => {
            if (files.length > 0) {
                showVariantsImageOptions(modelSide, files);
            }
        }, '.jpg,.png,.jpeg,.webp,.heic,.avif');
    };

    const showVariantsImageOptions = (modelSide: ModelSide, file: File[]) => {
        const code = findModelColorForModelSide(state.product, modelSide)?.code ?? '';
        const originalSide = state.sides.find((x) => x.guid === modelSide.originalSideGuid)!;
        showDialog(
            'apply-image-variant',
            <VariantDialog
                code={code}
                side={originalSide}
                type={'PRINT_AREA'}
                firstCallback={() => changeImage(modelSide, file[0], false, isDefaultImageScaled)}
                allCallback={() => handleApplyImageToAll(modelSide, file)}
                variantCallback={(color) => handleApplyImageToAllVariant(modelSide, color, file)}
            />
        );
    };

    const handleApplyImageToAll = (modelSide: ModelSide, files: File[]) => {
        if (modelSide) {
            state.product.colors.forEach((modelColor) => {
                const thisColorModelSide = modelColor.sides.find((x) => x.originalSideGuid == modelSide.originalSideGuid);

                if (thisColorModelSide) {
                    changeImage(thisColorModelSide, files[0], false, isDefaultImageScaled);
                }
            });
        }
    };

    const handleApplyImageToAllVariant = (modelSide: ModelSide, code: string, files: File[]) => {
        if (modelSide) {
            state.product.colors
                .filter((x) => {
                    const thisColorModel = JSON.parse(x.code) as Array<Color>;
                    let found = false;
                    for (let attribute of thisColorModel) {
                        if (attribute.Value.Id === code) {
                            found = true;
                        }
                    }
                    return found;
                })
                .forEach((modelColor) => {
                    const thisColorModelSide = modelColor.sides.find((x) => x.originalSideGuid == modelSide.originalSideGuid);
                    if (thisColorModelSide) {
                        changeImage(thisColorModelSide, files[0], false, isDefaultImageScaled);
                    }
                });
        }
    };

    const handleSideAreaChangeClick = (modelSide: ModelSide, modelColor: ModelColor) => {
        // Manually edit the url
        setCurrentEditingModelSide(modelSide);
        setCurrentEditingModelOptions(
            state.product.options.find((x) => x.colorGuid === modelColor.guid && (x.sideGuid === modelSide?.guid || x.sideGuid === null)) ?? null
        );

        navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/list/print-area-editor')), { state: location.state });
    };

    const handleApplyAreaToAll = (newModelSide: ModelSide, newModelOptions: ModelOptions | null) => {
        if (currentEditingModelSide) {
            state.product.colors.forEach((modelColor) => {
                const thisColorModelSide = modelColor.sides.find((x) => x.originalSideGuid == currentEditingModelSide.originalSideGuid);

                if (thisColorModelSide) {
                    dispatch(
                        mergeModelSideAction(thisColorModelSide, {
                            areas: newModelSide.areas.map((x) => ({
                                ...x,
                                layers: x.layers.map((y) => ({
                                    ...y,
                                    id: toGlobalID(uuid(), 'AreaLayer')
                                }))
                            })),
                            ppcm: newModelSide.ppcm,
                            canRotate: newModelSide.canRotate
                        })
                    );
                }

                if (newModelOptions) {
                    dispatch(upsertModelOptionsAction({ ...newModelOptions, colorGuid: modelColor.guid, optionID: null }));
                } else if (currentEditingModelOptions) {
                    dispatch(removeModelOptionsAction(modelColor));
                }
            });
        }
    };

    const handleApplyAreaToAllVariant = (newModelSide: ModelSide, newModelOptions: ModelOptions | null, code: string) => {
        if (currentEditingModelSide) {
            state.product.colors
                .filter((x) => {
                    const thisColorModel = JSON.parse(x.code) as Array<Color>;
                    let found = false;
                    for (let attribute of thisColorModel) {
                        if (attribute.Value.Id === code) {
                            found = true;
                        }
                    }
                    return found;
                })
                .forEach((modelColor) => {
                    const thisColorModelSide = modelColor.sides.find((x) => x.originalSideGuid == currentEditingModelSide.originalSideGuid);

                    if (thisColorModelSide) {
                        dispatch(
                            mergeModelSideAction(thisColorModelSide, {
                                areas: newModelSide.areas.map((x) => ({
                                    ...x,
                                    layers: x.layers.map((y) => ({
                                        ...y,
                                        id: toGlobalID(uuid(), 'AreaLayer')
                                    }))
                                })),
                                ppcm: newModelSide.ppcm,
                                canRotate: newModelSide.canRotate
                            })
                        );
                    }

                    if (newModelOptions) {
                        dispatch(upsertModelOptionsAction({ ...newModelOptions, colorGuid: modelColor.guid, optionID: null }));
                    } else if (currentEditingModelOptions) {
                        dispatch(removeModelOptionsAction(modelColor));
                    }
                });
        }
    };

    const showMultipleVariantsOption = (
        code: string,
        side: ModelSide,
        type: 'PRINT_AREA' | 'IMAGE',
        firstCallback: () => void,
        allCallback: () => void,
        variantCallback: (color: string) => void
    ) => {
        const originalSide = state.sides.find((x) => x.guid === side.originalSideGuid)!;
        showDialog(
            'apply-image-variant',
            <VariantDialog
                code={code}
                side={originalSide}
                type={type}
                firstCallback={() => firstCallback()}
                allCallback={() => allCallback()}
                variantCallback={(color) => variantCallback(color)}
            />
        );
    };

    const handleSideToggleClick = (colorGuid: string, sideGuid: string) => {
        let newDisabledSides = state.disabledModelSides;

        const existent = newDisabledSides.find((x) => x.colorGuid == colorGuid && x.sideGuid == sideGuid);

        // Check if the model side does not exist
        const modelColor = state.product.colors.find((x) => x.guid == colorGuid);
        let modelSide: ModelSide | null = null;

        if (modelColor) {
            modelSide = modelColor.sides.find((x) => x.originalSideGuid == sideGuid) || null;
        }

        // Check if the variant is not resizable, multi canvas or with border
        const isResizableOrMultiCanvasOrBorder = state.product.options.some(
            (x) => x.colorGuid === colorGuid && (x.isResizable || x.isSplitOutput || x.mirrorType !== 'NONE')
        );

        if (isResizableOrMultiCanvasOrBorder) {
            showDialog(
                'all-resizable-variants',
                <MessageDialog message={T._('You cannot add more sides because the variant is resizable, with border, or is multi-canvas.', 'Admin')} />
            );
            return;
        }

        if (existent || !modelSide) {
            // Enabled the side (and create the modelSide if does not exist)
            if (existent) {
                newDisabledSides = update(newDisabledSides, {
                    $splice: [[newDisabledSides.indexOf(existent), 1]]
                });
            }

            if (modelColor && !modelSide) {
                const side = state.sides.find((x) => x.guid == sideGuid);
                if (side) dispatch(addEmptyModelSideAction(modelColor, side));
            }
        } else {
            // Disable the side
            newDisabledSides = update(newDisabledSides, {
                $push: [{ colorGuid, sideGuid }]
            });
        }

        dispatch(setDisabledSidesAction(newDisabledSides));
    };

    const handleSetMandatorySide = (value: boolean, modelSide: ModelSide) => {
        const originalSide = state.sides.find((x) => x.guid === modelSide.originalSideGuid)!;
        showDialog(
            'apply-mandatory',
            <SideDialog
                side={originalSide}
                firstCallback={() => dispatch(mergeModelSideAction(modelSide, { ...modelSide, mandatory: value }))}
                allCallback={() => dispatch(editSideMandatoryAllSides(modelSide, value))}
                variantCallback={() => dispatch(editSideMandatoryAllVariants(modelSide, value))}
                dialogKey={'apply-mandatory'}
            />
        );
    };

    const handleSideChangeRotate = (value: boolean, modelSide: ModelSide) => {
        const originalSide = state.sides.find((x) => x.guid === modelSide.originalSideGuid)!;

        showDialog(
            'apply-rotate',
            <SideDialog
                side={originalSide}
                firstCallback={() => dispatch(mergeModelSideAction(modelSide, { ...modelSide, canRotate: value }))}
                allCallback={() => dispatch(editSideRotateAllSides(modelSide, value))}
                variantCallback={() => dispatch(editSideRotateAllVariants(modelSide, value))}
                dialogKey={'apply-rotate'}
            />
        );
    };

    const handleVariantAddClick = (showMapping?: boolean) => {
        if (state.ecommerceSalesChannel.some((e) => e === 'NONE') && !showMapping) {
            navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/scratch')));
            return;
        }

        showMapping === true ? setShowMappingDialog(true) : navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/add')));
    };

    const handlePreviewClick = () => {
        // First ensure that we will not remain with a color without any side
        const emptyColors = state.product.colors.filter((modelColor) => {
            const remainSidesLength = modelColor.sides
                .map((x) => x.originalSideGuid)
                .filter((x) => state.disabledModelSides.find((j) => j.colorGuid == modelColor.guid && j.sideGuid == x) === undefined).length;

            return remainSidesLength == 0;
        });

        if (emptyColors.length > 0) {
            showDialog(
                'empty-color',
                <MessageDialog message={stringFormat(T._(`Variation '{0}' should have at last one side enabled.`, 'Admin'), emptyColors[0].name)} />
            );
            return;
        }

        state.disabledModelSides.forEach((disabledSide) => {
            const modelColor = state.product.colors.find((x) => x.guid == disabledSide.colorGuid);
            if (modelColor) {
                const modelSide = modelColor.sides.find((side) => side.originalSideGuid == disabledSide.sideGuid);
                if (modelSide) dispatch(removeModelSideAction(modelSide));
            }
        });

        changeStep('preview');
    };

    const handleVariantMoveUpClick = (modelColor: ModelColor) => {
        // Take the index from the list and not sortOrder because it's more reliable
        const index = state.product.colors.indexOf(modelColor);

        if (index > 0) {
            const color1 = modelColor.guid;
            const color2 = state.product.colors[index - 1].guid;
            dispatch(swapColorsAction(color1, color2));
        }
    };

    const handleVariantMoveDownClick = (modelColor: ModelColor) => {
        // Take the index from the list and not sortOrder because it's more reliable
        const index = state.product.colors.indexOf(modelColor);

        if (index < state.product.colors.length - 1) {
            const color1 = modelColor.guid;
            const color2 = state.product.colors[index + 1].guid;
            dispatch(swapColorsAction(color1, color2));
        }
    };

    const onSaveVariants = () => {
        setModelOptions(() => {
            let modelColorsCodes = state.product.colors.map((c) => JSON.parse(c.code || '[]') as ColorAttribute[]);
            let modelOptions: merchantProductOptionType[] = [];
            if (state.product.colors.length > 0) {
                for (let i = 0; i < modelColorsCodes[0].length; i++) {
                    modelOptions.push({
                        isGlobal: false,
                        optionID: modelColorsCodes[0][i].Id,
                        label: modelColorsCodes[0][i].Label,
                        values: []
                    });
                    modelColorsCodes.forEach((m) => {
                        modelOptions[i].values.push({
                            valueID: m[i].Value.Id,
                            label: m[i].Value.Label
                        });
                    });
                    modelOptions[i].values = [...new Set(modelOptions[i].values.map((o) => JSON.stringify(o)))].map((o2) => JSON.parse(o2));
                }
            }
            return modelOptions;
        });

        navigate(getUrlWithQueryParams(backofficeUrl('/product/variations')));
    };

    const handleClickJsonSettings = (modelColor: ModelColor) => {
        showDialog(
            'json-settings',
            <JSONSettingsDialog
                modelColor={modelColor}
                sides={state.sides}
                onClose={() => closeDialog('json-settings')}
                onSave={(newModelColor: { settings: { colorProviderCode: string | null; sideCodes: { [sideGuid: string]: string } } }) => {
                    dispatch(editColorJsonSettingsAction(modelColor, newModelColor.settings));
                    closeDialog('json-settings');
                }}
            />
        );
    };

    useEffect(() => {
        if (showMappingDialog) {
            let supportedAdditionalCodesForVariants = state.product.additionalCodes.filter((ac) => {
                return !unsupportedSalesChannelsVariantsIDs.some((x) => toLocalID(x) === toLocalID(ac.salesChannelId));
            });

            showDialog(
                'mapping-variants',
                <ConnectModelDialog
                    windowDecorator={CustomDialogWindow}
                    forAddingNewSalesChannel={false}
                    forEditProduct={true}
                    buttonSaveText={T._('Save', 'Admin')}
                    productID={state.product.id ?? ''}
                    additionalCodes={supportedAdditionalCodesForVariants.map((c) => {
                        return { code: c.code, salesChannelID: c.salesChannelId, merchantProductName: c.merchantProductName };
                    })}
                    productColors={state.product.colors}
                    salesChannels={query.salesChannels as Mutable<PartialSalesChannel>[]}
                    selectedSalesChannelID={state.salesChannelID}
                    productSalesChannelID={state.salesChannelID}
                    modelColorMappings={state.product.colorMappings}
                    merchantProductOptionsConnected={merchantProductOptionsConnected as MerchantProductOption[]}
                    mainStoreVariantsRemoved={storeVariantsRemoved}
                    onCloseDialog={() => setShowMappingDialog(false)}
                    onConfirm={(product, salesChannelID, productColorMappingsToSave) => {
                        closeDialog('mapping-variants');
                        setShowMappingDialog(false);
                        dispatch(editModelColorMappingsSalesChannelsAction(productColorMappingsToSave));
                        navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/list/')));
                    }}
                />
            );
        } else closeDialog('mapping-variants');
    }, [showMappingDialog]);

    // Render
    return (
        <React.Fragment>
            {routeParams.page === 'variations' && !routeParams.subpage && (
                <>
                    {/* Variants not supported */}
                    {!areVariantsSupported && <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/variations/variants-not-supported'))} replace />}

                    {/* Variants not defined in the store */}
                    {areVariantsSupported && !hasVariants && (
                        <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/variations/no-store-variants'))} replace />
                    )}

                    {/* Variants defined correctly and at least one variant has been imported. */}
                    {areVariantsSupported && hasVariants && colors.length > 0 && (
                        <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/variations/list/'))} replace />
                    )}

                    {/* Variants defined correctly and no variant has been imported yet. */}
                    {areVariantsSupported && hasVariants && colors.length === 0 && (
                        <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/variations/add/'))} replace />
                    )}

                    {/* Menu soruce variants for scratch products */}
                    {state.ecommerceSalesChannel.some((e) => e === 'NONE') && !hasVariants && (
                        <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/variations/menu-source-variants'))} replace />
                    )}

                    {/* Resizable product */}
                    {getProductIsResizable(state.product) && query.seller?.marketplace !== 'HP' && (
                        <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/print-area'))} replace />
                    )}
                </>
            )}

            {routeParams.page === 'variations' &&
                routeParams.subpage === 'list' &&
                (colors.length > 0 || (state.ecommerceSalesChannel.some((e) => e === 'NONE') && state.ecommerceSalesChannel.length == 1)) && (
                    <>
                        <VariantsList
                            isSidesPerVariantEnabled={subscriptionResponse.subscription?.hasSidesPerVariant || false}
                            onSideToggleClick={handleSideToggleClick}
                            onSideAreaChangeClick={handleSideAreaChangeClick}
                            onSideImageChangeClick={handleSideImageChangeClick}
                            onVariantAddClick={handleVariantAddClick}
                            onVariantRemoveClick={handleVariantRemoveClick}
                            onVariantMoveDownClick={handleVariantMoveDownClick}
                            onVariantMoveUpClick={handleVariantMoveUpClick}
                            onVariantEditClick={handleVariantEditClick}
                            onPreviewClick={handlePreviewClick}
                            onSetMandatorySide={handleSetMandatorySide}
                            onSetRotateSide={handleSideChangeRotate}
                            onClickJsonSettings={handleClickJsonSettings}
                            unsupportedSalesChannelsVariantsIDs={unsupportedSalesChannelsVariantsIDs}
                            hasMappingMismatch={hasMappingMismatch}
                            mismatchedVariantGuids={mismatchedVariantGuids.current}
                            fixedVariantGuids={fixedVariantGuids}
                            storeVariantsRemoved={storeVariantsRemoved}
                        />

                        {currentEditingModelSide &&
                            routeParams.subpage2 === 'print-area-editor' &&
                            createPortal(
                                <PrintAreaEditorDialog
                                    modelColor={findModelColorForModelSide(state.product, currentEditingModelSide)!}
                                    modelSide={currentEditingModelSide}
                                    modelOptions={currentEditingModelOptions}
                                    disabledModelSides={state.disabledModelSides}
                                    onClose={() => {
                                        window.history.back();
                                    }}
                                    onSave={(newModelSide, newModelOptions) => {
                                        navigate(-1);
                                        const currentColor = findModelColorForModelSide(state.product, currentEditingModelSide)!;

                                        if (subscriptionResponse.subscription?.hasSidesPerVariant && state.product.colors.length > 1) {
                                            showMultipleVariantsOption(
                                                currentColor.code,
                                                currentEditingModelSide,
                                                'PRINT_AREA',
                                                () => {
                                                    dispatch(mergeModelSideAction(currentEditingModelSide, newModelSide));

                                                    // Model options
                                                    if (newModelOptions) {
                                                        dispatch(upsertModelOptionsAction(newModelOptions));
                                                    } else if (currentEditingModelOptions) {
                                                        dispatch(removeModelOptionsAction(currentColor));
                                                    }
                                                },
                                                () => handleApplyAreaToAll(newModelSide, newModelOptions),
                                                (code) => handleApplyAreaToAllVariant(newModelSide, newModelOptions, code)
                                            );
                                        } else {
                                            handleApplyAreaToAll(newModelSide, newModelOptions);
                                        }
                                    }}
                                />,
                                dialogsPortal
                            )}
                    </>
                )}

            {routeParams.page === 'variations' &&
                routeParams.subpage === 'list' &&
                colors.length === 0 &&
                state.ecommerceSalesChannel.some((e) => e === 'NONE') &&
                state.ecommerceSalesChannel.length == 1 && (
                    <>
                        <AddVariant
                            options={query.merchantProductOptions}
                            onAddVariantClick={handleAddVariationClick}
                            onCancelClick={() => navigate(-1)}
                            importImageVariantForSalesChannel={importImageVariantForSalesChannel}
                        />
                    </>
                )}

            {routeParams.page === 'variations' &&
                routeParams.subpage === 'list' &&
                colors.length === 0 &&
                !state.ecommerceSalesChannel.some((e) => e === 'NONE') &&
                state.ecommerceSalesChannel.length === 1 && (
                    <>
                        <Navigate to={getUrlWithQueryParams(backofficeUrl('/product/preview'))} replace />
                    </>
                )}

            {routeParams.page === 'variations' && routeParams.subpage === 'menu-source-variants' && (
                <>
                    <MenuSourceVariants
                        onChooseScratch={() => navigate(getUrlWithQueryParams(backofficeUrl('/product/variations/scratch')))}
                        onChooseImport={() => navigate(getUrlWithQueryParams(backofficeUrl('/product/variations')))}
                        productId={state.product.id || ''}
                    ></MenuSourceVariants>

                    {/* <EditVariantsOffline onSaveVariants={onSaveVariants} /> */}
                </>
            )}

            {routeParams.page === 'variations' && routeParams.subpage === 'scratch' && (
                <>
                    <EditVariantsOffline onSaveVariants={onSaveVariants} />
                </>
            )}

            {routeParams.page === 'variations' && routeParams.subpage === 'add' && (
                <>
                    <AddVariant
                        options={query.merchantProductOptions}
                        onAddVariantClick={handleAddVariationClick}
                        onCancelClick={() => navigate(-1)}
                        importImageVariantForSalesChannel={importImageVariantForSalesChannel}
                    />
                </>
            )}

            {routeParams.page === 'variations' && routeParams.subpage === 'no-store-variants' && (
                <>
                    <ContainerBase>
                        <Section>
                            <NoVariantsMessage>
                                <strong>Oops!</strong>
                                <br />
                                {T._(`It seems you haven't set any variants for this product in your store.`, 'Admin')}
                                <br />
                                {T._(`Zakeke gets the product variants directly from your store, please set them in the store first.`, 'Admin')}
                                <br />
                                <br />
                                <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/360015501299-Variants'>
                                    {T._('Learn more', 'Admin')}
                                </Link>
                            </NoVariantsMessage>
                        </Section>
                    </ContainerBase>
                    <PageFooterButtons $fixed={true}>
                        <Button onClick={handlePreviewClick}>{T._(`Go to preview`, 'Admin')}</Button>
                    </PageFooterButtons>
                </>
            )}

            {routeParams.page === 'variations' && routeParams.subpage === 'variants-not-supported' && (
                <>
                    <ContainerBase>
                        <Section>
                            <NoVariantsMessage>
                                <strong>Oops!</strong>
                                <br />
                                {T._(`Variants are not supported yet.`, 'Admin')}
                                <br />
                                {T._(`Please write us at`, 'Admin') + ' '}
                                <Link href={'mailto: help@zakeke.com'}>help@zakeke.com</Link>
                            </NoVariantsMessage>
                        </Section>
                    </ContainerBase>
                    <PageFooterButtons $fixed={true}>
                        <Button onClick={handlePreviewClick}>{T._(`Go to preview`, 'Admin')}</Button>
                    </PageFooterButtons>
                </>
            )}
        </React.Fragment>
    );
};

const VariantsError = () => {
    const changeStep = useProductConfigurationChangeStep();

    return (
        <>
            <ContainerBase>
                <Section>
                    <ErrorIcon>
                        <PageErrorIcon />
                    </ErrorIcon>
                    <NoVariantsMessage>
                        <strong>Oops!</strong>
                        <br />
                        {T._('It seems that this product has been removed from your store or is no longer accessible.', 'Admin')}
                        <br />
                        {T._('Please check the product in your store and try again.', 'Admin')}
                    </NoVariantsMessage>
                </Section>
            </ContainerBase>
            <PageFooterButtons $fixed={true} leftContainerContent={<TextButton onClick={() => changeStep('print-area')}>{T._('Edit', 'Admin')}</TextButton>}>
                <Button onClick={() => changeStep('preview')}>{T._('Go to preview', 'Admin')}</Button>
            </PageFooterButtons>
        </>
    );
};

const Variants = () => {
    return (
        <CustomErrorBoundary render={() => <VariantsError />}>
            <VariantsPage />
        </CustomErrorBoundary>
    );
};

export default Variants;
